import { AssetGroup } from '@play-co/timestep-core';
import StateObserver from 'src/StateObserver';
import { match2BundleSaleHasCurrentEvent } from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import { ViewOpts } from '@play-co/timestep-core/ui';
import { BundleSaleTheme } from 'src/replicant/match2/bundlesale/match2-bundlesale.ruleset';
import {
  ThemeProvider,
  createThemeProvider,
} from 'src/game/theme/ThemeProvider';
import { bundleSaleThemeDefault } from './themes/bundlesale.default';
import { getBundleSaleTheme } from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import { bundleSaleThemeXmas } from './themes/bundlesale.xmas';
import { bundleSaleThemeNewYear } from './themes/bundlesale.newyear';

/** Custom now() method that will return Date.now() if replicant is not ready */
function now() {
  if (!StateObserver || !StateObserver.replicant) {
    return Date.now();
  }
  return StateObserver.now();
}

export type BundleSaleThemeAssets = {
  arrow: string;
  lock: string;
  infinite: string;
  chest: string;
  check: string;
  card_normal: string;
  card_current: string;
  button_green: string;
  title_ja: string;
  title_en: string;
  header: string;
  snowflake: string;
  button: string;
};

export type BundleSaleThemeAssetKey = keyof BundleSaleThemeAssets;

export type BundleSaleThemeStyles = {
  header: ViewOpts;
  title_en: ViewOpts;
  title_ja: ViewOpts;
  timer: ViewOpts;
  info: ViewOpts;
  button_image: ViewOpts;
  button_timer: ViewOpts;
  snowflake: ViewOpts;
};

export type BundleSaleThemeStyleKey = keyof BundleSaleThemeStyles;

export type BundleSaleThemeProvider = {
  getStyle: (key: BundleSaleThemeStyleKey) => ViewOpts;
  getAsset: (key: BundleSaleThemeAssetKey) => string;
  getAssets: () => string[];
};

type ThemeProviderType = ThemeProvider<
  BundleSaleTheme,
  BundleSaleThemeProvider
>;

/** List of themes available */
const themes = {
  default: bundleSaleThemeDefault,
  xmas: bundleSaleThemeXmas,
  newyear: bundleSaleThemeNewYear,
};

/** Store providers per theme */
const providers: Partial<Record<BundleSaleTheme, ThemeProviderType>> = {};

/**
 * Retrieve the current bundle sale theme provider
 */
export function bundleSaleTheme(): ThemeProvider<
  BundleSaleTheme,
  BundleSaleThemeProvider
> {
  const theme = getBundleSaleTheme(now());
  if (!providers[theme]) {
    providers[theme] = createThemeProvider(themes, theme);
  }
  return providers[theme]!;
}

/** Get a list of all paths of current theme, for loading */
export function getBundleSaleAssetPaths() {
  if (!match2BundleSaleHasCurrentEvent(now())) return [];
  const list = bundleSaleTheme().getAssets();
  // Filter out empty assets, otherwise it will point to the entire asset folder
  return AssetGroup.constructURLs(list.filter((asset) => !!asset));
}
